import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { __core_private_testing_placeholder__ } from '@angular/core/testing';
import { ActivatedRoute } from '@angular/router';
import {
  Origin,
  Question,
  Configuration,
  Conversation,
  Message,
  ConfigurationLoader,
  Gateway,
  Option
} from 'serprobots';

/**
 * Componente principal da UI.
 * Agrupa todos os demais componentes da biblioteca do Serprobots em uma aplicação.
 * 
 * Funciona como um agrupador e orquestrador dos componentes. Por exemplo,
 * quando o componente MessageInputComponent lança o evento de "onNewMessage", este componente
 * captura este evento, envia a mensagem para o Gateway e informa ao componente MessageListComponent
 * que há uma nova mensagem a ser exibida.
 * 
 * Caso outras aplicações desejem criar uma UI própria mas reutilizando a UI do Serprobots, esta classe é o ponto 
 * de partida do que é necessário ser feito.
 */
@Component({
  selector: 'serprobots-ui',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  configuration: Configuration = new Configuration({});
  conversation: Conversation = new Conversation("1");

  // Mensagens padrão que o Serprobots entende como uma avaliação positiva ou negativa.
  private DISLIKE_MESSAGE = ":dislike:";
  private LIKE_MESSAGE = ":like:";

  // Precisamos da referência ao elemento HTML onde as mensagens são exibidas para fazer o scroll a cada nova mensagem.
  @ViewChild('messagesElementRef')
  private messagesElementRef!: ElementRef;

  // Mantém uma referência para o handler do setInterval para que ele possa ser finalizado em algum momento.
  private timeoutHandler!: any;

  // Informa se a próxima mensagem que o usuário enviar deve ser considerada como a última mensagem
  // que ele pode enviar. Após esta mensagem, a entrada de dados será desabilitada.
  private isWaitingFinalMessage: boolean = false;

  // Guardar o momento em que a última mensagem foi enviada.
  // Neste momento, reinicia o timer para verificar se deve encerrar a conversação.
  private lastMessageDate: Date = new Date();

  // Informa ao template se os métodos de entrada de texto devem ser desabilitados.
  disableInputs: boolean = false;

  // Informa ao template se a tela de avaliação deve ser exibida.
  showAssesment: boolean = false;

  // Informa ao template se o botão de abertura da tela de avaliação deve ser exibido.
  showAssesmentButton: boolean = false;

  // Informa se a avaliação está habilitada.
  enableAssesment: boolean = true;

  // Informa quando foi enviada a última mensagem de polling.
  lastPollingMessage: Date = new Date();

  // Guarda os parâmetros da QueryString para que sejam encaminhados junto a cada mensagem enviada para o Gateway.
  private queryParameters: any = {};

  // Identifica se já existe uma mensagem sendo enviada. 
  // Evita o envio de mensagens enquanto ainda existe uma sendo processada.
  private isSending: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private configurationLoader: ConfigurationLoader,
    private gateway: Gateway
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.configuration = await this.configurationLoader.getConfiguration();
    this.enableAssesment = this.configuration.assesmentShowButton;
    
    const params = this.activatedRoute.snapshot.queryParamMap;

    for (let key of params.keys) {
      this.queryParameters[key] = params.get(key);
    }

    await this.startConversation();
  }

  /**
   * Inicia um timer que será executado a cada um segundo. Este timer checará:
   * 
   * 1. Se já deve finalizar a conversa, conforme o tempo definido nas configurações.
   * 2. Se deve fazer o polling e enviar a mensagem de polling pro Gateway.
   * 
   * Caso seja o momento de finalizar a conversação, então, envia a mensagem de Timeout para o Gateway,
   * conforme definido nas configurações. 
   * 
   * Caso seja o momento de enviar a mensagem de polling, então, a envia para o Gateway mas não finaliza
   * a conversação.
   * 
   * O setInterval é concluído com o clearInterval no método endConversation() quando a conversação é finalizada por algum motivo.
   */
  private async startTimer(): Promise<void> {   
    this.timeoutHandler = setInterval(async () => {
      const now = new Date();

      // O resultado é em milisegundos, dividir por 1000 e arredondar o valor para obter em segundos.
      var difference = Math.abs(((now.getTime() - this.lastMessageDate.getTime()) / 1000));

      // Verificar se é pra encerrar a conversação por timeout.
      if (difference >= this.configuration.timeoutSeconds! && !this.isSending) {
        this.disableInputs = true;

        await this.sendQuestion(this.configuration.timeoutMessage!);
        await this.endConversation();
      }

      // Verificar se deve enviar a mensagem de polling.
      difference = Math.abs(((now.getTime() - this.lastPollingMessage.getTime()) / 1000));

      if (this.configuration.pollingSeconds! > 0 && difference >= this.configuration.pollingSeconds! && !this.isSending) {
        this.lastPollingMessage = new Date();
        await this.sendQuestion(this.configuration.pollingMessage!);
      }
    }, 1000);
  }

  /**
   * Cria um Message com o texto de erro para Ratelimit e a adiciona na conversação,
   * para que apareça na tela.
   */
  showRateLimitMessage(): void {
    let message = Message.of(this.configuration.rateLimitErrorMessage!);
    message.isError = true;
    this.conversation.addMessage(message);
  }

  /**
   * Cria um Message com o texto de erro geral e a adiciona na conversação,
   * para que apareça na tela.
   */
  showUnknownError(): void {
    let message = Message.of(this.configuration.unknownErrorMessage!);
    message.isError = true;
    this.conversation.addMessage(message);
  }

  /**
   * Envia uma pergunta do usuário para o Gateway e a adiciona à conversação.
   * 
   * Desabilita o campo de entrada de texto enquanto a mensagem estiver sendo enviada, com o objetivo
   * de evitar o envio de múltiplas mensagens enquanto o Gateway ainda não tiver dado um resposta para
   * a pergunta anterior.
   * 
   * Trata a exibição de mensagens de erro quando há um erro não identificado (status 500) ou quando
   * há um erro de Rate Limit (status 429).
   * 
   * @param text Pergunta que deve ser enviada para o Gateway.
   * @param showMessage Informa se a mensagem retornada do Gateway deve ser exibida na lista de mensagens.
   * @returns Resposta para a pergunta.
   */
  async sendQuestion(text: string, showMessage: Boolean = true): Promise<Message> {
    try {
      if (this.conversation.isFinalized) {
        throw new Error('Conversa já finalizada. Não é possível enviar novas mensagens.');
      }

      if (!this.isSending) {
        this.disableInputs = true;
        this.isSending = true;

        var answer = await this.gateway.answer(new Question(this.conversation.id, text, this.queryParameters));
        
        if (showMessage) {
          this.conversation.addMessage(answer);
        }

        this.isSending = false;
        this.disableInputs = false;

        if (answer.skipUserInput()) {
          answer = await this.sendQuestion("", true);
        }

        return answer;
      } else {
        throw { message: 'Já existe uma mensagem sendo enviada. Aguarde até que esteja finalizada!', continueConversation: true };
      }
    } catch (error: any) {

      // Caso seja um HttpStatus 429, é um erro de Rate Limit enviado pelo Gateway.
      if (error.status && error.status == 429) {
        this.showRateLimitMessage();
      } else if ((error.status == 0) || (error.status && error.status >= 400)) {

        // Exibir uma mensagem genérica para os demais erros 400 e 500.
        this.showUnknownError();
      }

      // Nem toda exceção que é capturada aqui deve finalizar a conversação.
      // Pode ser uma exceção apenas para evitar o envio de mensagens duplicadas.
      if (!error.continueConversation) {
        this.endConversation();
      }

      throw new Error(error.message);
    }
  }

  /**
   * Trata o evento de envio de mensagem, que é emitido pelo componente de MessageInputComponent.
   * 
   * @param userMessage Mensagem enviada pelo usuário.
   */
  async onNewUserMessage(userMessage: string): Promise<void> {
    await this.handleNewUserMessage(userMessage);
  }

  /**
   * Cria uma Question com o texto enviado como parâmetro e envia para o Gateway.
   * 
   * Após obter a resposta, adiciona esta resposta ao componente MessageListComponent.
   * Define o CheckTimeout para True, pois deve-se verificar novamente se o Timeout foi alcançado.
   * 
   * Verifica se é a última mensagem que deve ser enviada para o Gateway. Se sim, finaliza a conversação.
   * 
   * @param userMessage Mensagem enviada pelo usuário.
   */
  private async handleNewUserMessage(userMessage: string, origin: Origin = Origin.USER): Promise<void> {
    let message = Message.of(userMessage);
    message.origin = origin;
    this.conversation.addMessage(message);

    await this.sendQuestion(userMessage);
    await this.cleanUpAfterSendingNewUserMessage();
  }

  /**
   * Realiza todas as tarefas necessárias para colocar a tela em um estado válido logo após enviar uma mensagem do usuário para o Gateway.
   */
  private async cleanUpAfterSendingNewUserMessage(): Promise<void> {
    this.lastMessageDate = new Date();

    if (this.isWaitingFinalMessage) {
      this.endConversation();
    } else {

      // O botão de avaliação deve ser exibido sempre que houver mais de uma pergunta realizada pelo usuário.
      // Se o usuário não fez pergunta alguma, não faz sentido pedir uma avaliação.
      this.showAssesmentButton = this.conversation.messages.length > 1;
    }
  }

  /**
   * Faz o scroll até a última mensagem enviada pelo usuário, 
   * para permitir que a resposta do chatbot seja visualizada desde o início
   */
  scrollToLastUserMessage(): void {
    try {

      var scrollHeight = 0
      const incomingMessages = this.messagesElementRef.nativeElement.querySelectorAll(`.${Origin.USER}.message-list-content`)
      if (incomingMessages.length > 0) {
        const lastIncomingMessage = incomingMessages[incomingMessages.length - 1]
        scrollHeight = lastIncomingMessage.offsetTop - (lastIncomingMessage.scrollHeight - 20)
      }

      this.messagesElementRef.nativeElement.scrollTo({ left: 0, top: scrollHeight, behavior: 'smooth' });

    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Realiza a inicialização da conversação.
   * 
   * Verifica se já existe uma conversação ativa, buscando essa informação no localStorage.
   * Caso exista, solicita ao Gateway o histórico.
   * 
   alStorage, inicia uma nova.
   * 
   * Caso o Gateway retorne que a conversação foi finalizada, inicia uma nova.
   * 
   * @param forced Informa se deve ignorar a existência de uma conversação guardada no Storage
   *  e forçar uma nova, ignorando a existente.
   */
  private async startConversation(forced: Boolean = false): Promise<void> {
    try {
      if (!forced && this.hasStoredConversation()) {
        this.restoreConversation(this.getStoredConversation()!!);
      } else {
        this.conversation = new Conversation("123");
        this.conversation.id = await this.gateway.createConversationId(this.queryParameters);
        
        this.storeConversation(this.conversation.id);
  
        await this.sendQuestion(this.configuration.initialMessage || "");  
      }

      await this.startTimer();

      this.disableInputs = false;
      this.lastMessageDate = new Date();
      this.lastPollingMessage = new Date();
      this.showAssesmentButton = false;
      this.isWaitingFinalMessage = false;
    } catch (error: any) {

      // Caso seja um HttpStatus 429, é um erro de Rate Limit enviado pelo Gateway.
      if (error.status && error.status == 429) {
        this.showRateLimitMessage();
      } else if ((error.status == 0) || (error.status && error.status >= 400)) {

        // Exibir uma mensagem genérica para os demais erros 400 e 500.
        this.showUnknownError();
      }

      // Nem toda exceção que é capturada aqui deve finalizar a conversação.
      // Pode ser uma exceção apenas para evitar o envio de mensagens duplicadas.
      if (!error.continueConversation) {
        this.endConversation();
      }

      throw new Error(error.message);
    }

    this.conversation.onNewMessage().subscribe(
      () => {

        // Timeout necessário, pois o momento em que a mensagem é adicionada
        // à conversação não é o mesmo momento que ela é exibida na tela.
        // Desta forma, o Height do componente pode estar errado.
        setTimeout(() => {
          this.scrollToLastUserMessage();
        }, 100);
      }
    );
  }

  /**
   * Salva o identificador da conversação para que seja reutilizado posteriormente.
   * 
   * @param conversationId Identificador da conversação.
   */
  private storeConversation(conversationId?: string) {
    if (this.isLocalStorageAvailable()){
      if(conversationId) {
        window.localStorage.setItem("serprobots.conversation.id", conversationId);
      } else {
        window.localStorage.removeItem("serprobots.conversation.id");
      }
    }
  }

  /**
   * Verifica se existe uma conversação armazenada para uso posterior.
   * 
   * @returns True caso exista uma conversação armazenada. False, caso contrário.
   */
  private hasStoredConversation(): Boolean {   
    if (this.isLocalStorageAvailable()) {
      const conversationId = window.localStorage.getItem("serprobots.conversation.id");
      return conversationId != null && conversationId != "";
    } else {
      return false;
    }
  }

  private isLocalStorageAvailable(): Boolean {
    try {
      if (typeof window.localStorage !== 'undefined') {        
        localStorage.setItem('feature_test', 'yes');

        if (localStorage.getItem('feature_test') === 'yes') {
          localStorage.removeItem('feature_test');
          return true;
        } else {
          console.warn("Não foi possível usar o Local Storage.");
          return false;
        }        
      } else {
        console.warn("Não foi possível usar o Local Storage.");
        return false;
      }
    } catch(e) {
      console.warn("Não foi possível usar o Local Storage.");
      return false;
    }
  }

  /**
   * Retorna o ID de uma conversação que foi armazenada para uso posterior.
   * 
   * @returns ID da conversação restaurada.
   */
  private getStoredConversation(): string | null {
    if(this.isLocalStorageAvailable()){
      const conversationId = window.localStorage.getItem("serprobots.conversation.id");
      return conversationId;
    }else{
      return null;
    }
  }

  /**
   * Restaura uma conversação a partir de seu identificador.
   * 
   * @param conversationId Identificador da conversação que deve ser restaurada.
   */
  private async restoreConversation(conversationId: string): Promise<void> {
    this.conversation = new Conversation(conversationId);

    this.gateway.getConversationHistory(conversationId).then((messages: Message[]) => {
      messages.forEach(message => {
        if (message && message.question && message.question != this.configuration.initialMessage) {
          const msg = Message.of(message.question);
          msg.origin = Origin.USER;
          msg.date = message.date;
          this.conversation.addMessage(msg);
        } else {
          message.origin = Origin.BOT;
        }
  
        this.conversation.addMessage(message);

        this.showAssesmentButton = true;
      });  
    }).catch((error: any) => {
      this.storeConversation();
      this.startConversation();
    });    
  }

  /**
   * Reinicia a conversação.
   */
  async onRestart(restart: boolean): Promise<void> {
    await this.startConversation(true);
  }

  /** 
   * Trata o evento de "opção selecionada" lançado pelo componente MessageListComponent.
   * 
   * Adiciona o label do Option como uma mensagem do usuário e depois envia o valor do Option
   * como uma pergunta para o Gateway.
   * 
   * @param option Opção selecionada pelo usuário.
   */
  async onOptionSelected(option: Option): Promise<void> {
    let userMessage = Message.of(option.label);
    userMessage.origin = Origin.USER;
    this.conversation.addMessage(userMessage);

    await this.sendQuestion(option.value);
    this.cleanUpAfterSendingNewUserMessage();
  }

  /**
 * Evento lançado pelo componente HeaderComponent e que solicita que a janela do chatbot
 * seja fechada. Existem dois comportamentos possíveis:
 * 
 * 1. Caso o parâmetro headerOnCloseRedirectTo da configuração esteja definido, a janela deve ser redirecionada para este endereço;
 * 2. Caso o parâmetro headerOnCloseRedirectTo não esteja definido, deve repassar a solicitação para o iframe.
 */
  async onClose(): Promise<void> {
    if (this.configuration.headerOnCloseRedirectTo) {
      window.location.href = this.configuration.headerOnCloseRedirectTo!;
    } else {

      // Neste caso, consideramos que a janela do chatbot foi aberta em um iframe
      // dentro de algum site externo. Enviamos apenas uma mensagem usando postMessage().
      // Esta mensagem será capturada pelo Javascript do Trigger, que fechará o iframe.
      //
      // O uso do postMessage() é para evitar problemas com CORS. Caso tente chamar diretamente o parent,
      // com window.parent, ocorrerá um erro de CORS. Para evitar este problema, deve-se usar postMessage().
      window.parent.postMessage('close', '*');
    }
  }

  /**
   * Evento lançado pelo componente HeaderComponent e que solicita que a janela do chatbot
   * seja minimizada.
   * 
   * Esta solicitação será repassada para o iframe que contém o chatbot através do método postMessage do DOM.
   */
  async onMinimize(): Promise<void> {

    // Neste caso, consideramos que a janela do chatbot foi aberta em um iframe
    // dentro de algum site externo. Enviamos apenas uma mensagem usando postMessage().
    // Esta mensagem será capturada pelo Javascript do Trigger, que fechará o iframe.
    //
    // O uso do postMessage() é para evitar problemas com CORS. Caso tente chamar diretamente o parent,
    // com window.parent, ocorrerá um erro de CORS. Para evitar este problema, deve-se usar postMessage().
    window.parent.postMessage('minimize', '*');
  }

  /**
   * Trata o evento lançado quando o usuário avalia a conversação de forma positiva.
   * Este evento é lançado pelo componente AssesmentComponent.
   * 
   * Envia a mensagem para o Gateway e finaliza a conversação logo em seguida.
   */
  async onLikeClicked(): Promise<void> {
    let message = Message.of("Gostei do atendimento!");
    message.origin = Origin.USER;
    this.conversation.addMessage(message);

    await this.sendQuestion(this.LIKE_MESSAGE);
    this.endConversation();
  }

  /**
   * Trata o evento lançado quando o usuário avalia a conversação de forma negativa.
   * Este evento é lançado pelo componente AssesmentComponent.
   * 
   * Envia a mensagem para o Gateway mas não finaliza a conversação, pois ainda 
   * aguardará mais uma entrada de texto do usuário, informando o motivo de não ter gostado
   * do atendimento.
   */
  async onDislikeClicked(): Promise<void> {
    let message = Message.of("Não gostei do atendimento!");
    message.origin = Origin.USER;
    this.conversation.addMessage(message);

    await this.sendQuestion(this.DISLIKE_MESSAGE);
    this.showAssesment = false;
    this.showAssesmentButton = false;
    this.isWaitingFinalMessage = true;
  }

  /**
   * Trata o evento que solicita a exibição da tela de avaliação.
   * Este evento é lançado pelo componente AssesmentComponent.
   */
  onShowAssesment(): void {
    this.showAssesment = true;
  }

  /**
   * Repassa o evento de solicitação de envio de uma mensagem text para os inscritos para receberem esse evento.
   * 
   * @param text Texto a ser repassado.
   */
   async onRequestSendText(text: string): Promise<void> {
    let userMessage = Message.of(text);
    userMessage.origin = Origin.USER;
    this.conversation.addMessage(userMessage);

    await this.sendQuestion(text);
    this.cleanUpAfterSendingNewUserMessage();
  }
  
  /**
   * Realiza todas as tarefas necessárias para finalizar a conversação ativa.
   * Não permite mais a entrada de dados, forçando o usuário a reiniciar a conversação.
   */
  async endConversation(): Promise<void> {
    try {
      await this.gateway.deleteConversation(this.conversation.id);
    } catch (error) {
      console.error("Serprobots: Não foi possível finalizar a conversação.");
      console.error(error);
    } finally { 
      clearInterval(this.timeoutHandler);

      this.conversation.finalize();
      this.showAssesment = false;
      this.disableInputs = true;
      this.isSending = false;
      this.showAssesmentButton = false;
      this.isWaitingFinalMessage = false;

      if (this.isLocalStorageAvailable()) {
        window.localStorage.removeItem("serprobots.conversation.id");
      }
    }
  }

}
